/**
 * Google Tag Manager dataLayer helpers.
 *
 * GTM only initializes in production (see main.tsx), but pushes are never
 * guarded: the container replays whatever is already in the array when it
 * loads, so events emitted before gtm.js finishes downloading still fire.
 */

export type DataLayerEvent = Record<string, unknown> & { event: string };

/** Both payment paths count as a conversion; kept so they can be told apart downstream. */
export type OrderPaymentMethod = 'credit_card' | 'purchase_order';

export type OrderSource = 'portal_checkout' | 'quick_turn_checkout';

// A type alias, not an interface: only aliases get an implicit index signature,
// which pushDataLayerEvent's Record<string, unknown> parameter requires.
export type OrderCreatedMeta = {
    /** Null only if the store lost the order reference; the conversion still counts. */
    order_id: number | string | null;
    payment_method: OrderPaymentMethod;
    source: OrderSource;
};

function getDataLayer(): DataLayerEvent[] {
    // Cast instead of the ambient Window type: this package declares
    // Window.dataLayer twice, as an object and as an array.
    const target = (window as unknown) as { dataLayer?: DataLayerEvent[] };

    if (!Array.isArray(target.dataLayer)) {
        target.dataLayer = [];
    }

    return target.dataLayer;
}

export function pushDataLayerEvent(event: string, payload: Record<string, unknown> = {}): void {
    if (!event) {
        return;
    }

    try {
        // event goes last so a stray payload.event cannot rename the GTM trigger.
        getDataLayer().push({ ...payload, event });
    } catch (err) {
        console.error(`[GTM] Failed to push "${event}":`, err);
    }
}

export function trackOrderCreated(meta: OrderCreatedMeta): void {
    pushDataLayerEvent('order_created', meta);
}
